import type { FormikErrors } from 'formik'
import { Field } from 'formik'
import findIndex from 'lodash-es/findIndex'
import isNil from 'lodash-es/isNil'
import type { FocusEvent } from 'react'
import React from 'react'

import type { TableHeaders } from '@fourthwall/components'
import { CurrencyInput, PriceString } from '@fourthwall/components'
import type { Offer } from '@fourthwall/services/dist/models/offer'
import { PRODUCT_PRICE_MAX } from '@fourthwall/utils/dist/constants'
import { formatCurrency } from '@fourthwall/utils/dist/currency'

import type { ApplyToAllVariantsProps, ProductFormState } from '../../SingleProduct.types'
import { ErrorMessage } from '../components/ErrorMessage/ErrorMessage'
import { PricingTableCell } from '../components/PricingTableCell/PricingTableCell'

type GetTableHeadersProps = {
  variants: Offer.Variant[]
  errors: FormikErrors<ProductFormState>
  applyToAllVariants: (props: ApplyToAllVariantsProps) => void
  showCompareAtPrice: boolean
  showProfit: boolean
  disabled: boolean
}

const findInputName = (variants: Offer.Variant[], size?: string, property = 'price') => {
  const index = findIndex(variants, (variant) => variant.size === size)

  return `variants[${index}].${property}.value`
}

export const getTableHeaders = ({
  variants,
  errors,
  applyToAllVariants,
  disabled,
  showCompareAtPrice,
  showProfit,
}: GetTableHeadersProps): TableHeaders<Offer.Variant> => {
  const tableHeaders: TableHeaders<Offer.Variant> = [
    {
      id: 'size',
      label: 'Size',
      minWidth: 100,
      minHeight: 72,
      alignItems: 'baseline',
      render: ({ size }) => (
        <PricingTableCell mainContent={<div style={{ paddingTop: '12px' }}>{size}</div>} />
      ),
    },
    {
      id: 'price',
      label: 'Selling price',
      minWidth: 120,
      minHeight: 72,
      alignItems: 'baseline',
      render: ({ size }, index) => {
        // @ts-ignore
        const error = errors.variants?.[index]?.price?.value

        return (
          <PricingTableCell
            mainContent={
              <div style={{ maxWidth: 120 }}>
                <Field
                  component={CurrencyInput}
                  size="medium"
                  name={findInputName(variants, size)}
                  displayError={false}
                  disabled={disabled}
                  onBlur={(e: FocusEvent<HTMLInputElement>) =>
                    applyToAllVariants({ priceValue: e.target.value, size })
                  }
                />
                {error && <ErrorMessage>{error}</ErrorMessage>}
              </div>
            }
          />
        )
      },
    },
    {
      id: 'compareAtPrice',
      label: 'Compare-at price',
      alignItems: 'baseline',
      render: ({ size }, index) => {
        // @ts-ignore
        const error = errors.variants?.[index]?.compareAtPrice?.value

        return (
          <PricingTableCell
            mainContent={
              <div style={{ maxWidth: 120 }}>
                <Field
                  component={CurrencyInput}
                  size="medium"
                  name={findInputName(variants, size, 'compareAtPrice')}
                  disabled={disabled}
                  displayError={false}
                  error={error}
                  strikethrough
                  min={0}
                  max={PRODUCT_PRICE_MAX}
                  onBlur={(e: FocusEvent<HTMLInputElement>) =>
                    applyToAllVariants({ compareAtPriceValue: e.target.value || null, size })
                  }
                />
                {error && <ErrorMessage>{error}</ErrorMessage>}
              </div>
            }
          />
        )
      },
      hidden: !showCompareAtPrice,
    },
    {
      id: 'profit',
      label: 'Profit per sale',
      minWidth: 100,
      minHeight: 72,
      alignItems: 'baseline',
      hidden: !showProfit,
      render: (variant) => {
        const cost = variant.cost?.value

        const profit = !isNil(cost) ? formatCurrency(variant.price.value - cost) : 0

        return (
          <PricingTableCell
            mainContent={
              <div style={{ paddingTop: '12px' }}>
                {!isNil(cost) ? <PriceString value={profit} /> : <>&ndash;</>}
              </div>
            }
          />
        )
      },
    },
  ]

  return tableHeaders.filter((header) => !header.hidden)
}
